import { sortBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { TabModules, useModulesConfig } from '~/data/company';
import { DevelopmentTabs, PeopleTabs, RewardingTabs } from '~/data/tabs';
import { AdminWorkspaceRoles } from '~/data/user/role';
import { MyTeamWidgetProps } from '~/scenes/MyWorkspace/components/Widgets/components/MyTeamWidget/component';
import { BottomActionButtonsProps } from '~/scenes/MyWorkspace/components/Widgets/components/MyTeamWidget/components/BottomActions/component';
import { getWidgetDataRequestRoles } from '~/scenes/MyWorkspace/components/Widgets/data';
import { People } from '~/services/Http';
import { useGetEmployeeTeam } from '~/services/Http/hooks';
import { AppModules } from '~/services/Permissions';

export interface UseMyTeamWidgetOutput {
  loading: boolean;
  failedToLoad: boolean;
  teamMembers?: People.TeamMemberDto[];
  activeMemberId?: number;
  showDetailsForMember?: number;
  handleActiveMemberChange: (index: number) => void;
  handleShowMemberDetails: (employeeId: number) => void;
  handleCloseMemberDetails: () => void;
  bottomActionButtonsProps: BottomActionButtonsProps;
}

export const useMyTeamWidget = (
  config: MyTeamWidgetProps['config'],
): UseMyTeamWidgetOutput => {
  const [activeMemberId, setActiveMemberId] = useState<number>();
  const [showDetailsForMember, setShowDetailsForMember] = useState<number>();

  const dataRequestRoles = getWidgetDataRequestRoles(config?.role);

  const {
    loading,
    failedToLoad,
    data: teamMembers,
  } = useGetEmployeeTeam({
    employeeId: 'me',
    roles: dataRequestRoles,
  });

  useEffect(() => {
    if (!loading && teamMembers?.length) {
      setActiveMemberId(teamMembers[0].employeeId);
    }
  }, [loading, teamMembers, setActiveMemberId]);

  const handleCloseMemberDetails = useCallback(() => {
    setShowDetailsForMember(undefined);
  }, [setShowDetailsForMember]);

  const { isModuleEnabled, isModuleTabEnabled } = useModulesConfig();

  const rewardingModuleRoleAccess =
    config?.role !== AdminWorkspaceRoles.DEPUTY_MANAGER;

  const bottomActionButtonsProps: BottomActionButtonsProps = {
    activeMemberId,
    absence: {
      disabled: !isModuleTabEnabled(TabModules.PEOPLE, PeopleTabs.Absences),
      show: true,
    },
    equipment: {
      disabled: !isModuleTabEnabled(TabModules.PEOPLE, PeopleTabs.Equipment),
      show: true,
    },
    education: {
      disabled: !isModuleTabEnabled(
        TabModules.DEVELOPMENT,
        DevelopmentTabs.Education,
      ),
      show: true,
    },
    rewarding: {
      disabled: !isModuleTabEnabled(
        TabModules.REWARDING,
        RewardingTabs.General,
      ),
      show: rewardingModuleRoleAccess,
    },
    tasks: {
      disabled: false,
      show: isModuleEnabled(AppModules.COLLABORATION),
    },
  };

  const sortedTeamMembers = useMemo(() => {
    return sortBy(teamMembers, (member) => [member.lastName, member.firstName]);
  }, [teamMembers]);

  return {
    loading,
    failedToLoad,
    teamMembers: sortedTeamMembers,
    activeMemberId,
    showDetailsForMember,
    handleActiveMemberChange: setActiveMemberId,
    handleShowMemberDetails: setShowDetailsForMember,
    handleCloseMemberDetails,
    bottomActionButtonsProps,
  };
};
